import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { KeyboardHelp } from './KeyboardHelp';
import { CommandPalette } from './CommandPalette';
import { BulkImportDock } from './BulkImportDock';
import { IdleWarningModal } from './IdleWarningModal';
import { useNavigationShortcuts } from '../lib/keyboard';
import { useIdleTimeout } from '../lib/useIdleTimeout';
import { useAuth } from '../lib/auth';

// 30 minutes of inactivity signs the user out (Bug-0022), with a 60s warning.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const IDLE_WARNING_MS = 60 * 1000;

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  // Inactivity auto-logout: sign out + redirect to login with an expiry note.
  async function signOutIdle(message: string) {
    try {
      await logout();
    } finally {
      navigate('/dashboard/login', { replace: true, state: { flash: message } });
    }
  }
  const idle = useIdleTimeout({
    timeoutMs: IDLE_TIMEOUT_MS,
    warningMs: IDLE_WARNING_MS,
    onTimeout: () =>
      void signOutIdle('You were signed out after 30 minutes of inactivity.'),
  });

  // Auto-close drawer on navigation, and move keyboard focus to <main>
  // so screen-reader + keyboard users land on the new page content
  // instead of staying parked on the nav link they just clicked. The
  // <main> already has tabIndex=-1 and id="main-content" so it's a
  // valid focus target without becoming a Tab stop.
  useEffect(() => {
    setDrawerOpen(false);
    if (typeof document === 'undefined') return;
    const main = document.getElementById('main-content');
    if (main) main.focus({ preventScroll: true });
    // Reset scroll on top of route changes too — without this the new
    // page inherits the previous page's scroll position.
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname]);

  // Lock body scroll while drawer is open on mobile
  useEffect(() => {
    if (drawerOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [drawerOpen]);

  // Escape closes the drawer (the help modal handles its own escape)
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  // Register global g-prefix navigation shortcuts + ? for help
  const shortcuts = useNavigationShortcuts({
    onOpenHelp: () => setHelpOpen(true),
    onCloseHelp: () => setHelpOpen(false),
    isHelpOpen: helpOpen,
  });

  // Cmd-K / Ctrl-K opens the command palette. Bound separately from the
  // g-prefix shortcuts because it must fire even while focus is inside an
  // input field — that's what makes a quick switcher useful.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isPaletteCombo =
        (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (isPaletteCombo) {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex h-screen flex-col bg-[var(--color-bg)]">
      <Header
        onOpenDrawer={() => setDrawerOpen(true)}
        onOpenHelp={() => setHelpOpen(true)}
      />
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop sidebar — always visible at md+ */}
        <aside
          className="hidden w-[268px] shrink-0 overflow-y-auto border-r border-[var(--color-rule)] bg-[var(--color-bg-deep)] md:block"
          aria-label="Primary navigation"
        >
          <Sidebar />
        </aside>

        {/* Mobile drawer — slides in from the left */}
        {drawerOpen && (
          <>
            <button
              type="button"
              aria-label="Close navigation"
              onClick={() => setDrawerOpen(false)}
              className="fixed inset-0 z-[var(--z-overlay)] cursor-pointer bg-[var(--color-navy)]/40 backdrop-blur-[2px] md:hidden"
            />
            <aside
              role="dialog"
              aria-modal="true"
              aria-label="Primary navigation"
              className="fixed left-0 top-0 z-[var(--z-modal)] h-full w-[280px] max-w-[85vw] overflow-y-auto border-r border-[var(--color-rule)] bg-[var(--color-bg-deep)] shadow-[8px_0_24px_-8px_rgba(25,54,97,0.18)] md:hidden prism-enter"
            >
              <Sidebar onNavigate={() => setDrawerOpen(false)} />
            </aside>
          </>
        )}

        {/* Main content shell */}
        <main
          id="main-content"
          className="flex-1 overflow-y-auto"
          tabIndex={-1}
        >
          <div className="mx-auto max-w-[1440px] px-5 py-8 sm:px-8 sm:py-12 lg:px-12 lg:py-14 prism-enter">
            {children}
          </div>
        </main>
      </div>

      <KeyboardHelp
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
        shortcuts={shortcuts}
      />

      <CommandPalette
        isOpen={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        commands={shortcuts}
      />

      {/* Global, persistent indicator that a bulk resume import is running —
          visible on every page, survives navigation + refresh. */}
      <BulkImportDock />

      {idle.warning && (
        <IdleWarningModal
          secondsLeft={idle.secondsLeft}
          onStay={idle.stayActive}
          onSignOut={() => void signOutIdle('You have been signed out.')}
        />
      )}
    </div>
  );
}

export function SkipLink() {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[var(--z-toast)] focus:rounded-[4px] focus:bg-[var(--color-primary)] focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white"
    >
      Skip to main content
    </a>
  );
}
